/**
 * DATX High-Performance Client Cache & SWR Utility
 * Provides instant 0ms retrieval with background revalidation and cross-session persistence
 */

const memoryCache = new Map();

export const clientCache = {
  get(key) {
    // 1. Fast in-memory lookup
    const mem = memoryCache.get(key);
    if (mem && Date.now() < mem.expiry) {
      return mem.data;
    }

    // 2. Fallback to sessionStorage for page navigations
    try {
      const stored = sessionStorage.getItem(`datx_cache_${key}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Date.now() < parsed.expiry) {
          memoryCache.set(key, parsed);
          return parsed.data;
        }
      }
    } catch {}

    return null;
  },

  set(key, data, ttlSeconds = 300) {
    const item = {
      data,
      expiry: Date.now() + ttlSeconds * 1000,
    };
    memoryCache.set(key, item);
    try {
      sessionStorage.setItem(`datx_cache_${key}`, JSON.stringify(item));
    } catch {}
  },

  clear(keyPrefix) {
    if (!keyPrefix) {
      memoryCache.clear();
      try {
        const keys = Object.keys(sessionStorage);
        keys.forEach((k) => {
          if (k.startsWith('datx_cache_')) sessionStorage.removeItem(k);
        });
      } catch {}
      return;
    }

    for (const k of memoryCache.keys()) {
      if (k.startsWith(keyPrefix)) memoryCache.delete(k);
    }
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach((k) => {
        if (k.startsWith(`datx_cache_${keyPrefix}`)) sessionStorage.removeItem(k);
      });
    } catch {}
  },

  async fetchWithSWR(key, fetcher, ttlSeconds = 300) {
    const cached = this.get(key);
    if (cached !== null) {
      // Stale-While-Revalidate: Return cached immediately, refresh background
      fetcher()
        .then((fresh) => {
          if (fresh !== undefined && fresh !== null) {
            this.set(key, fresh, ttlSeconds);
          }
        })
        .catch(() => {});
      return cached;
    }

    const fresh = await fetcher();
    if (fresh !== undefined && fresh !== null) {
      this.set(key, fresh, ttlSeconds);
    }
    return fresh;
  },
};
